/**
 * cvt-gsap-animator.ts - Módulo Canônico de Timeline GSAP do Variador Primário
 * Domínio: Bancada Técnica Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Padrão Concessionária Honda Dream & Filosofia Fábio Akita (Deep Modules, Zero Mocks e Fail-Visible)
 */

export interface CvtPhaseInfo {
  readonly phaseNumber: number; // 1 a 5
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly telemetryTag: string;
  readonly startTime: number; // segundos
  readonly endTime: number; // segundos
  readonly duration: number; // segundos
  readonly rpm: number;
  readonly torqueSpecNm?: number;
  readonly specialTool?: string;
  readonly gotchaNote: string;
  readonly description: string;
}

export interface CvtPhaseTimestamp {
  readonly phaseNumber: number;
  readonly name: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly duration: number;
  readonly startPercent: number; // 0 a 100
  readonly endPercent: number; // 0 a 100
}

export interface CvtTimelineProgress {
  readonly phase: CvtPhaseInfo;
  readonly phaseNumber: number;
  readonly progressInPhase: number; // 0.0 a 1.0
  readonly globalProgress: number; // 0.0 a 1.0
  readonly currentTime: number;
  readonly totalDuration: number;
  readonly speed: number;
  readonly isPlaying: boolean;
}

export interface CvtPartTransformState {
  readonly partId: string;
  readonly translateX: number;
  readonly translateY: number;
  readonly rotationDeg: number;
  readonly opacity: number;
  readonly isHighlighted: boolean;
}

export interface CvtTimelineState {
  readonly currentTime: number;
  readonly speed: number;
  readonly isPlaying: boolean;
  readonly activePhase: CvtPhaseInfo;
  readonly phaseProgress: number;
  readonly globalProgress: number;
  readonly toolEngaged: boolean;
  readonly torqueShockwaveActive: boolean;
  readonly transforms: Record<string, CvtPartTransformState>;
}

export interface GsapTweenDescriptor {
  readonly target: string;
  readonly duration: number;
  readonly vars: Record<string, unknown>;
  readonly position: number | string;
}

export interface GsapTimelineConfig {
  readonly totalDuration: number;
  readonly labels: Record<string, number>;
  readonly tweens: ReadonlyArray<GsapTweenDescriptor>;
}

/**
 * Definição Canônica Oficial das 5 Fases da Transmissão Primária Honda ADV 150
 */
export const CVT_OFFICIAL_PHASES: ReadonlyArray<CvtPhaseInfo> = [
  {
    phaseNumber: 1,
    id: "phase-1-idle",
    name: "Marcha Lenta (1.700 RPM)",
    title: "Operação em Marcha Lenta (1.700 RPM)",
    telemetryTag: "[FASE 1/5] OPERAÇÃO EM MARCHA LENTA (1.700 RPM) – RELAÇÃO FECHADA",
    startTime: 0.0,
    endTime: 2.0,
    duration: 2.0,
    rpm: 1700,
    gotchaNote:
      "Relação inicial curta com roletes no repouso centrífugo e correia na base da bucha.",
    description:
      "Variador primário montado girando na marcha lenta oficial (1.700 RPM). Relação de transmissão mais curta para arrancada suave.",
  },
  {
    phaseNumber: 2,
    id: "phase-2-disassembly",
    name: "Desmontagem Axial Escalonada (10 a 1)",
    title: "Desmontagem Axial Escalonada (10 a 1)",
    telemetryTag: "[FASE 2/5] DESMONTAGEM AXIAL ESCALONADA: PORCA M14 DESROSQUEIA (10 ➔ 1)",
    startTime: 2.0,
    endTime: 7.0,
    duration: 5.0,
    rpm: 0,
    gotchaNote:
      "Porca M14 desrosqueia 720° no sentido anti-horário. Desmontagem sequencial 10 a 1 sem forçar estrias do virabrequim.",
    description:
      "Desaperto da porca M14 (720° anti-horário) seguido pelo recuo axial escalonado: Belleville, face fixa ventilada, espaçador, correia Bando, bucha boss, polia móvel, 6 roletes 21g e placa de rampa.",
  },
  {
    phaseNumber: 3,
    id: "phase-3-inspection",
    name: "Freeze Metrológico & Inspeção OEM",
    title: "Freeze Metrológico & Inspeção OEM",
    telemetryTag: "[FASE 3/5] FREEZE-FRAME TÉCNICO: INSPEÇÃO DE COTAS & METROLOGIA OEM",
    startTime: 7.0,
    endTime: 11.0,
    duration: 4.0,
    rpm: 0,
    gotchaNote:
      "MONTAGEM 100% A SECO DOS ROLETES. Limite de correia: 18,5 mm; roletes: 19,5 mm; bucha: 23,96 mm.",
    description:
      "Pausa técnica analítica com balões ISO 128 e realce neon nas cotas de desgaste, tolerâncias centesimais e limites de serviço de fábrica.",
  },
  {
    phaseNumber: 4,
    id: "phase-4-assembly-belt-sinking",
    name: "Remontagem Axial & Protocolo Belt Sinking",
    title: "Remontagem Axial & Protocolo Belt Sinking",
    telemetryTag: "[FASE 4/5] REMONTAGEM AXIAL REVERSA & PROTOCOLO BELT SINKING (FOLGA OK)",
    startTime: 11.0,
    endTime: 14.0,
    duration: 3.0,
    rpm: 0,
    gotchaNote:
      "GOTCHA FATAL: Afundar a correia com as duas mãos na polia secundária traseira para garantir folga na frente antes do encosto da face fixa.",
    description:
      "Remontagem axial sequencial 1 a 10 aplicando rigorosamente o protocolo Belt Sinking: alívio da correia Bando prevenindo falso torque e quebra do eixo.",
  },
  {
    phaseNumber: 5,
    id: "phase-5-torque-59nm",
    name: "Trava com Ferramenta em Y & Torque 59 N.m",
    title: "Trava com Ferramenta em Y & Torque 59 N.m",
    telemetryTag: "[FASE 5/5] FERRAMENTA EM Y TRAVADA ➔ TORQUE 59 N.m APLICADO NO ESTALO!",
    startTime: 14.0,
    endTime: 16.0,
    duration: 2.0,
    rpm: 0,
    torqueSpecNm: 59,
    specialTool: "Universal Holder Honda 07725-0030000 + Soquete 22 mm",
    gotchaNote:
      "TORQUE CANÔNICO: 59 N.m (6,0 kgf.m). Proibido talhadeira ou aperto por impacto sem torquímetro calibrado.",
    description:
      "Pinos do Universal Holder 07725-0030000 acoplam nos 2 furos reforçados da face fixa; aplicação do torque canônico de 59 N.m com estalo e choque acústico.",
  },
];

export const CVT_TIMELINE_TOTAL_DURATION_SEC = 16.0;
export const CVT_MIN_SPEED = 0.25;
export const CVT_MAX_SPEED = 2.0;
export const CVT_DEFAULT_SPEED = 1.0;

/**
 * CvtGsapAnimator - Controlador e Motor de Cálculo da Timeline GSAP
 */
export class CvtGsapAnimator {
  private speed = CVT_DEFAULT_SPEED;
  private currentTime = 0.0;
  private playing = false;
  private readonly totalDuration = CVT_TIMELINE_TOTAL_DURATION_SEC;
  private readonly phases: ReadonlyArray<CvtPhaseInfo>;

  constructor(
    phases: ReadonlyArray<CvtPhaseInfo> = CVT_OFFICIAL_PHASES,
    initialSpeed: number = CVT_DEFAULT_SPEED,
    initialTime = 0.0,
  ) {
    if (!phases || phases.length === 0) {
      throw new Error("CvtGsapAnimator: lista de fases oficiais não pode ser vazia.");
    }
    this.phases = phases;
    this.setSpeed(initialSpeed);
    this.setCurrentTime(initialTime);
  }

  /**
   * Define o multiplicador de velocidade da animação, com suporte estrito de 0.25x a 2.0x.
   * Fail-visible para entradas inválidas (NaN, valores não numéricos ou <= 0).
   */
  public setSpeed(multiplier: number): number {
    if (
      typeof multiplier !== "number" ||
      Number.isNaN(multiplier) ||
      !Number.isFinite(multiplier) ||
      multiplier <= 0
    ) {
      throw new Error(
        `CvtGsapAnimator.setSpeed: multiplicador inválido: ${multiplier}. Deve ser um número finito positivo.`,
      );
    }

    const clamped = Math.max(CVT_MIN_SPEED, Math.min(CVT_MAX_SPEED, multiplier));
    this.speed = Number(clamped.toFixed(2));
    return this.speed;
  }

  public getSpeed(): number {
    return this.speed;
  }

  public getTotalDuration(): number {
    return this.totalDuration;
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public setCurrentTime(time: number): number {
    if (typeof time !== "number" || Number.isNaN(time) || !Number.isFinite(time)) {
      throw new Error(`CvtGsapAnimator.setCurrentTime: timestamp inválido: ${time}`);
    }
    this.currentTime = Math.max(0, Math.min(this.totalDuration, time));
    return this.currentTime;
  }

  public isPlaying(): boolean {
    return this.playing;
  }

  public play(): void {
    this.playing = true;
  }

  public pause(): void {
    this.playing = false;
  }

  public togglePlay(): boolean {
    this.playing = !this.playing;
    return this.playing;
  }

  public getPhases(): ReadonlyArray<CvtPhaseInfo> {
    return this.phases;
  }

  /**
   * Retorna os timestamps de início e fim e durações das 5 fases.
   */
  public getPhaseTimestamps(): ReadonlyArray<CvtPhaseTimestamp> {
    return this.phases.map((p) => {
      const startPercent = Number(((p.startTime / this.totalDuration) * 100).toFixed(2));
      const endPercent = Number(((p.endTime / this.totalDuration) * 100).toFixed(2));
      return {
        phaseNumber: p.phaseNumber,
        name: p.name,
        startTime: p.startTime,
        endTime: p.endTime,
        duration: p.duration,
        startPercent,
        endPercent,
      };
    });
  }

  /**
   * Determina qual das 5 Fases Oficiais está ativa em um dado instante de tempo.
   */
  public getPhaseAt(time: number): CvtPhaseInfo {
    const clampedTime = Math.max(0, Math.min(this.totalDuration, time));

    // No ponto final exato (16.0s), pertence à Fase 5
    if (clampedTime >= this.totalDuration) {
      const last = this.phases[this.phases.length - 1];
      if (last) return last;
    }

    for (const phase of this.phases) {
      if (clampedTime >= phase.startTime && clampedTime < phase.endTime) {
        return phase;
      }
    }

    // Fallback seguro caso o timestamp atinja exatamente a borda final
    const defaultPhase = this.phases[this.phases.length - 1] ?? this.phases[0];
    if (defaultPhase) return defaultPhase;
    throw new Error("CvtGsapAnimator: nenhuma fase encontrada para o timestamp especificado.");
  }

  /**
   * Move a timeline diretamente para o início da fase especificada (1 a 5).
   * Fail-visible se phaseNumber estiver fora do intervalo [1, 5].
   */
  public seekToPhase(phaseNumber: number): number {
    if (!Number.isInteger(phaseNumber) || phaseNumber < 1 || phaseNumber > this.phases.length) {
      throw new Error(
        `CvtGsapAnimator.seekToPhase: número de fase fora dos limites [1, ${this.phases.length}]: ${phaseNumber}`,
      );
    }

    const targetPhase = this.phases[phaseNumber - 1];
    if (!targetPhase) {
      throw new Error(
        `CvtGsapAnimator.seekToPhase: fase de índice ${phaseNumber - 1} não encontrada.`,
      );
    }

    this.currentTime = targetPhase.startTime;
    return this.currentTime;
  }

  /**
   * Avança para a próxima fase. Se estiver na Fase 5, reinicia na Fase 1.
   */
  public nextPhase(): CvtPhaseInfo {
    const current = this.getPhaseAt(this.currentTime);
    const nextNumber = current.phaseNumber >= 5 ? 1 : current.phaseNumber + 1;
    this.seekToPhase(nextNumber);
    return this.getPhaseAt(this.currentTime);
  }

  /**
   * Retrocede para a fase anterior. Se estiver na Fase 1, vai para a Fase 5.
   */
  public prevPhase(): CvtPhaseInfo {
    const current = this.getPhaseAt(this.currentTime);
    const prevNumber = current.phaseNumber <= 1 ? 5 : current.phaseNumber - 1;
    this.seekToPhase(prevNumber);
    return this.getPhaseAt(this.currentTime);
  }

  /**
   * Calcula o progresso fracionário local (0 a 1) na fase ativa e o progresso global (0 a 1).
   */
  public calculatePhaseProgress(time: number): CvtTimelineProgress {
    const clampedTime = Math.max(0, Math.min(this.totalDuration, time));
    const phase = this.getPhaseAt(clampedTime);

    let progressInPhase = 0.0;
    if (phase.duration > 0) {
      progressInPhase = (clampedTime - phase.startTime) / phase.duration;
      progressInPhase = Math.max(0, Math.min(1, progressInPhase));
    }

    const globalProgress = Math.max(0, Math.min(1, clampedTime / this.totalDuration));

    return {
      phase,
      phaseNumber: phase.phaseNumber,
      progressInPhase,
      globalProgress,
      currentTime: clampedTime,
      totalDuration: this.totalDuration,
      speed: this.speed,
      isPlaying: this.playing,
    };
  }

  /**
   * Avanço/retrocesso temporal com suporte total a reversibilidade (deltaTime > 0 ou < 0)
   * e escalonamento pelo multiplicador de velocidade configurado.
   */
  public step(deltaTimeSeconds: number, wrapAround = true): number {
    const effectiveDelta = deltaTimeSeconds * this.speed;

    if (wrapAround) {
      let nextTime = this.currentTime + effectiveDelta;
      if (nextTime >= this.totalDuration) {
        nextTime = nextTime % this.totalDuration;
      } else if (nextTime < 0) {
        nextTime = this.totalDuration - (Math.abs(nextTime) % this.totalDuration);
        if (nextTime === this.totalDuration) nextTime = 0;
      }
      this.currentTime = Number(nextTime.toFixed(4));
    } else {
      this.currentTime = Number(
        Math.max(0, Math.min(this.totalDuration, this.currentTime + effectiveDelta)).toFixed(4),
      );
    }

    return this.currentTime;
  }

  /**
   * Calcula de forma analítica e reversível os offsets axiais e rotações de cada peça
   * para um instante de tempo t, espelhando os keyframes da animação SVG oficial.
   */
  public calculateTransformsAt(time: number): Record<string, CvtPartTransformState> {
    const clampedTime = Math.max(0, Math.min(this.totalDuration, time));
    const transforms: Record<string, CvtPartTransformState> = {};

    // Helper de interpolação linear suave
    const lerp = (start: number, end: number, t: number): number =>
      start + (end - start) * Math.max(0, Math.min(1, t));

    // Determina a fração de deslocamento axial dos slots móveis conforme a fase
    // Na Fase 1 (0..2s): Totalmente montado (translateX negativo correspondente ao recuo axial)
    // Na Fase 2 (2..7s): Desmontagem axial progressiva (retorna de recuo montado até 0px explodido)
    // Na Fase 3 (7..11s): Freeze explodido (0px)
    // Na Fase 4 (11..14s): Remontagem axial progressiva (retorna de 0px até o recuo montado)
    // Na Fase 5 (14..16s): Totalmente montado
    let disassemblyRatio = 0.0; // 0 = montado, 1 = explodido

    if (clampedTime <= 2.0) {
      disassemblyRatio = 0.0;
    } else if (clampedTime > 2.0 && clampedTime <= 7.0) {
      disassemblyRatio = (clampedTime - 2.0) / 5.0; // 0 -> 1
    } else if (clampedTime > 7.0 && clampedTime <= 11.0) {
      disassemblyRatio = 1.0;
    } else if (clampedTime > 11.0 && clampedTime <= 14.0) {
      disassemblyRatio = 1.0 - (clampedTime - 11.0) / 3.0; // 1 -> 0
    } else {
      disassemblyRatio = 0.0;
    }

    // Configuração dos recuos montados oficiais em relação aos slots do XML SVG
    const partOffsets: Record<string, { mountedX: number; explodedX: number }> = {
      "part-01-crankshaft": { mountedX: 0, explodedX: 0 },
      "part-02-ramp-plate": { mountedX: -48, explodedX: 0 },
      "part-03-rollers": { mountedX: -125, explodedX: 0 },
      "part-04-movable-face": { mountedX: -185, explodedX: 0 },
      "part-05-boss-bushing": { mountedX: -255, explodedX: 0 },
      "part-06-drive-belt": { mountedX: -335, explodedX: 0 },
      "part-07-spacer-washer": { mountedX: -385, explodedX: 0 },
      "part-08-fixed-face": { mountedX: -430, explodedX: 0 },
      "part-09-belleville-washer": { mountedX: -580, explodedX: 0 },
      "part-10-nut-m14": { mountedX: -665, explodedX: 0 },
    };

    // Rotação da porca M14 (720° anti-horário na desmontagem)
    let nutRotation = 0;
    if (clampedTime > 2.0 && clampedTime <= 7.0) {
      nutRotation = lerp(0, -720, (clampedTime - 2.0) / 5.0);
    } else if (clampedTime > 7.0 && clampedTime <= 11.0) {
      nutRotation = -720;
    } else if (clampedTime > 11.0 && clampedTime <= 14.0) {
      nutRotation = lerp(-720, 0, (clampedTime - 11.0) / 3.0);
    } else {
      nutRotation = 0;
    }

    // Elevação da correia na Fase 3
    let beltY = 0;
    if (disassemblyRatio > 0.8) {
      beltY = -12 * ((disassemblyRatio - 0.8) / 0.2);
    }

    for (const [partId, config] of Object.entries(partOffsets)) {
      const translateX = Number(
        lerp(config.mountedX, config.explodedX, disassemblyRatio).toFixed(2),
      );
      const translateY = partId === "part-06-drive-belt" ? beltY : 0;
      const rotationDeg = partId === "part-10-nut-m14" ? Number(nutRotation.toFixed(1)) : 0;
      const opacity = 1.0;
      const isHighlighted =
        clampedTime >= 7.0 && clampedTime <= 11.0
          ? partId === "part-03-rollers" || partId === "part-06-drive-belt"
          : partId === "part-10-nut-m14" && clampedTime >= 14.0;

      transforms[partId] = {
        partId,
        translateX,
        translateY,
        rotationDeg,
        opacity,
        isHighlighted,
      };
    }

    return transforms;
  }

  /**
   * Retorna o snapshot completo de estado no instante atual.
   */
  public getTimelineState(time = this.currentTime): CvtTimelineState {
    const progress = this.calculatePhaseProgress(time);
    const transforms = this.calculateTransformsAt(time);

    // Ferramenta em Y engata entre 14.08s e 15.36s (88% a 96% do ciclo)
    const toolEngaged = time >= 14.08 && time <= 15.36;

    // Onda de choque luminosa de 59 N.m ativa entre 14.72s e 15.36s (92% a 96%)
    const torqueShockwaveActive = time >= 14.72 && time <= 15.36;

    return {
      currentTime: progress.currentTime,
      speed: this.speed,
      isPlaying: this.playing,
      activePhase: progress.phase,
      phaseProgress: progress.progressInPhase,
      globalProgress: progress.globalProgress,
      toolEngaged,
      torqueShockwaveActive,
      transforms,
    };
  }

  /**
   * Gera a configuração estruturada de tweens e labels para exportação ao GSAP oficial.
   */
  public generateGsapTimelineConfig(): GsapTimelineConfig {
    const labels: Record<string, number> = {};
    for (const phase of this.phases) {
      labels[phase.id] = phase.startTime;
    }

    const tweens: GsapTweenDescriptor[] = [
      // Fase 1: Marcha Lenta (1.700 RPM)
      {
        target: ".tech-diagram",
        duration: 2.0,
        vars: { opacity: 1, ease: "none" },
        position: 0,
      },
      // Fase 2: Desmontagem Axial
      {
        target: ".anim-piece-nut",
        duration: 5.0,
        vars: { x: 0, rotation: -720, ease: "power2.inOut" },
        position: 2.0,
      },
      {
        target: ".anim-piece-belleville",
        duration: 4.5,
        vars: { x: 0, ease: "power2.inOut" },
        position: 2.2,
      },
      {
        target: ".anim-piece-fixed-face",
        duration: 4.2,
        vars: { x: 0, ease: "power2.inOut" },
        position: 2.4,
      },
      {
        target: ".anim-piece-belt",
        duration: 3.8,
        vars: { x: 0, y: -12, ease: "power2.inOut" },
        position: 2.8,
      },
      // Fase 3: Freeze Metrológico
      {
        target: ".callout-balloon-group",
        duration: 4.0,
        vars: { opacity: 1, scale: 1.05, ease: "sine.inOut" },
        position: 7.0,
      },
      // Fase 4: Remontagem Axial & Belt Sinking
      {
        target: ".anim-piece-belt",
        duration: 2.5,
        vars: { x: -335, y: 0, ease: "power2.out" },
        position: 11.0,
      },
      {
        target: ".anim-piece-fixed-face",
        duration: 2.8,
        vars: { x: -430, ease: "power2.out" },
        position: 11.2,
      },
      {
        target: ".anim-piece-nut",
        duration: 3.0,
        vars: { x: -665, rotation: 0, ease: "power2.out" },
        position: 11.0,
      },
      // Fase 5: Trava com Ferramenta em Y e Torque 59 N.m
      {
        target: ".holder-pins-tool",
        duration: 1.2,
        vars: { opacity: 1, y: 0, ease: "back.out(1.7)" },
        position: 14.1,
      },
      {
        target: ".torque-shock-ring",
        duration: 0.6,
        vars: { opacity: 1, scale: 1.2, ease: "expo.out" },
        position: 14.7,
      },
    ];

    return {
      totalDuration: this.totalDuration,
      labels,
      tweens,
    };
  }

  /**
   * Renderiza a barra de controles touch de bancada compatível com padrão Dark OLED (>= 48dp).
   */
  public renderControlBarHtml(): string {
    const state = this.getTimelineState();
    const phase = state.activePhase;

    return `
<div class="cvt-gsap-controller" style="background: #060911; border: 1px solid #1E293B; border-radius: 12px; padding: 16px; color: #F8FAFC; font-family: system-ui, sans-serif;">
  <!-- Cabeçalho de Telemetria -->
  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 8px;">
    <div style="display: flex; align-items: center; gap: 8px;">
      <span style="background: #0284C7; color: #FFF; font-family: monospace; font-size: 11px; font-weight: 800; padding: 3px 8px; border-radius: 4px;">GSAP TIMELINE</span>
      <span style="font-weight: 800; font-size: 14px; color: #F8FAFC;">Fase ${phase.phaseNumber}/5: ${phase.name}</span>
    </div>
    <div style="font-family: monospace; font-size: 12px; color: #38BDF8;">
      Tempo: ${state.currentTime.toFixed(2)}s / ${this.totalDuration.toFixed(1)}s (${(state.globalProgress * 100).toFixed(0)}%)
    </div>
  </div>

  <!-- Barra Scrub de Progresso Temporal Interativo -->
  <div style="margin-bottom: 14px;">
    <input type="range" class="cvt-scrub-slider" min="0" max="${this.totalDuration}" step="0.05" value="${state.currentTime}" style="width: 100%; height: 8px; accent-color: #00F2FE; cursor: pointer;">
    <div style="display: flex; justify-content: space-between; font-family: monospace; font-size: 10px; color: #94A3B8; margin-top: 4px;">
      <span>0s (F1: Lenta)</span>
      <span>2s (F2: Desmonte)</span>
      <span>7s (F3: Metrologia)</span>
      <span>11s (F4: Remontagem)</span>
      <span>14s (F5: 59 N.m)</span>
      <span>16s</span>
    </div>
  </div>

  <!-- Botões de Fase Rápida -->
  <div style="display: flex; gap: 6px; margin-bottom: 14px; overflow-x: auto; scrollbar-width: none;">
    ${this.phases
      .map(
        (p) => `
      <button type="button" data-seek-phase="${p.phaseNumber}" style="min-height: 48px; min-width: 48px; flex: 1; padding: 8px 10px; background: ${p.phaseNumber === phase.phaseNumber ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid ${p.phaseNumber === phase.phaseNumber ? "#38BDF8" : "#334155"}; border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; white-space: nowrap;">
        Fase ${p.phaseNumber}
      </button>
    `,
      )
      .join("")}
  </div>

  <!-- Gotcha de Oficina da Fase -->
  <div style="background: #111827; border: 1px solid #1E3A8A; border-radius: 8px; padding: 10px 12px; margin-bottom: 14px; font-size: 12px; color: #CBD5E1;">
    <div style="color: #FDE047; font-weight: 700; margin-bottom: 2px;">⚡ Gotcha de Bancada:</div>
    <div>${phase.gotchaNote}</div>
  </div>

  <!-- Controles Principais de Execução e Velocidade (Thumb Zone >= 48dp) -->
  <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;">
    <div style="display: flex; gap: 8px;">
      <button type="button" data-action="prev-phase" style="min-height: 48px; min-width: 48px; padding: 8px 16px; background: #1E293B; color: #FFF; border: 1px solid #475569; border-radius: 8px; font-weight: 700; cursor: pointer;">⏮️ Anterior</button>
      <button type="button" data-action="toggle-play" style="min-height: 48px; min-width: 90px; padding: 8px 16px; background: ${state.isPlaying ? "#EAB308" : "#10B981"}; color: #000; border: 1px solid #FFF; border-radius: 8px; font-weight: 800; cursor: pointer;">
        ${state.isPlaying ? "⏸️ Pausar" : "▶️ Play"}
      </button>
      <button type="button" data-action="next-phase" style="min-height: 48px; min-width: 48px; padding: 8px 16px; background: #1E293B; color: #FFF; border: 1px solid #475569; border-radius: 8px; font-weight: 700; cursor: pointer;">Próxima ⏭️</button>
    </div>

    <!-- Velocidades (0.25x a 2x) -->
    <div style="display: flex; align-items: center; gap: 6px;">
      <span style="font-family: monospace; font-size: 11px; color: #94A3B8;">Velocidade:</span>
      ${[0.25, 0.5, 1.0, 1.5, 2.0]
        .map(
          (spd) => `
        <button type="button" data-speed="${spd}" style="min-height: 48px; min-width: 48px; padding: 6px 10px; background: ${Math.abs(this.speed - spd) < 0.05 ? "#0284C7" : "#1E293B"}; color: #FFF; border: 1px solid ${Math.abs(this.speed - spd) < 0.05 ? "#38BDF8" : "#334155"}; border-radius: 6px; font-size: 11px; font-weight: 700; cursor: pointer;">
          ${spd}x
        </button>
      `,
        )
        .join("")}
    </div>
  </div>
</div>
`.trim();
  }
}
